import React from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { DirhamIconColored } from '../DirhamIcon';
import { Toggle } from './CategoryModal';

const OfferModal = ({
  language,
  isAdmin,
  editingItem,
  form,
  setForm,
  services,
  saving,
  onSave,
  onClose,
}) => {
  const togglePill = (val, fld) =>
    setForm({
      ...form,
      [fld]: form[fld].includes(val) ? form[fld].filter((id) => id !== val) : [...form[fld], val],
    });

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-card border border-white/10 rounded-2xl p-6 max-w-lg w-full my-8">
        <h3 className="text-xl font-bold text-white mb-4">
          {editingItem
            ? language === 'ar' ? 'تعديل العرض' : 'Edit Offer'
            : language === 'ar' ? 'إضافة عرض جديد' : 'Add New Offer'}
        </h3>

        {!isAdmin && (
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 mb-4">
            <p className="text-sm text-yellow-500">
              {language === 'ar'
                ? 'سيتم إرسال العرض للمدير للموافقة قبل نشره على الصفحة الرئيسية'
                : 'This offer will be sent to admin for approval before appearing on homepage'}
            </p>
          </div>
        )}

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                {language === 'ar' ? 'العنوان بالعربية' : 'Title (Arabic)'} *
              </label>
              <input
                type="text"
                value={form.title_ar}
                onChange={(e) => setForm({ ...form, title_ar: e.target.value })}
                className="w-full bg-zinc-800/50 border border-white/10 rounded-lg px-4 py-2 text-white"
                placeholder={language === 'ar' ? 'مثال: عرض الصيف' : 'e.g., Summer Offer'}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                {language === 'ar' ? 'العنوان بالإنجليزية' : 'Title (English)'} *
              </label>
              <input
                type="text"
                value={form.title_en}
                onChange={(e) => setForm({ ...form, title_en: e.target.value })}
                className="w-full bg-zinc-800/50 border border-white/10 rounded-lg px-4 py-2 text-white"
                placeholder="e.g., Summer Offer"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                {language === 'ar' ? 'الوصف بالعربية' : 'Description (Arabic)'}
              </label>
              <textarea
                value={form.description_ar}
                onChange={(e) => setForm({ ...form, description_ar: e.target.value })}
                className="w-full bg-zinc-800/50 border border-white/10 rounded-lg px-4 py-2 text-white resize-none"
                rows={2}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                {language === 'ar' ? 'الوصف بالإنجليزية' : 'Description (English)'}
              </label>
              <textarea
                value={form.description_en}
                onChange={(e) => setForm({ ...form, description_en: e.target.value })}
                className="w-full bg-zinc-800/50 border border-white/10 rounded-lg px-4 py-2 text-white resize-none"
                rows={2}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              {language === 'ar' ? 'الخدمات المشمولة (اختياري)' : 'Included Services (Optional)'}
            </label>
            <div className="max-h-32 overflow-y-auto bg-zinc-800/50 border border-white/10 rounded-lg p-2">
              {services.filter((s) => s.is_active).map((service) => (
                <label key={service.id} className="flex items-center gap-2 p-2 hover:bg-white/5 rounded cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.service_ids.includes(service.id)}
                    onChange={() => togglePill(service.id, 'service_ids')}
                    className="accent-primary"
                  />
                  <span className="text-white text-sm">
                    {language === 'ar' ? service.name_ar : service.name_en}
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                {language === 'ar' ? 'السعر الأصلي' : 'Original Price'}{' '}
                <DirhamIconColored className="w-3 h-3 inline" color="white" /> *
              </label>
              <input
                type="number"
                value={form.original_price}
                onChange={(e) => setForm({ ...form, original_price: e.target.value })}
                className="w-full bg-zinc-800/50 border border-white/10 rounded-lg px-4 py-2 text-white"
                min="0"
                step="0.01"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                {language === 'ar' ? 'سعر العرض (د.إ)' : 'Offer Price (AED)'} *
              </label>
              <input
                type="number"
                value={form.discount_price}
                onChange={(e) => setForm({ ...form, discount_price: e.target.value })}
                className="w-full bg-zinc-800/50 border border-white/10 rounded-lg px-4 py-2 text-white"
                min="0"
                step="0.01"
              />
            </div>
          </div>

          {form.original_price && form.discount_price && (
            <div className="text-center text-green-500 font-bold">
              {language === 'ar' ? 'نسبة الخصم: ' : 'Discount: '}
              {Math.round((1 - parseFloat(form.discount_price) / parseFloat(form.original_price)) * 100)}%
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                {language === 'ar' ? 'تاريخ البداية' : 'Start Date'} *
              </label>
              <input
                type="date"
                value={form.start_date}
                onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                className="w-full bg-zinc-800/50 border border-white/10 rounded-lg px-4 py-2 text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                {language === 'ar' ? 'تاريخ النهاية' : 'End Date'} *
              </label>
              <input
                type="date"
                value={form.end_date}
                onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                className="w-full bg-zinc-800/50 border border-white/10 rounded-lg px-4 py-2 text-white"
              />
            </div>
          </div>

          {isAdmin && (
            <div className="border-t border-white/10 pt-4 mt-4 space-y-3">
              <p className="text-sm font-medium text-muted-foreground mb-2">
                {language === 'ar' ? 'إعدادات الظهور' : 'Visibility Settings'}
              </p>
              <div className="flex items-center justify-between">
                <label className="text-sm text-foreground">
                  {language === 'ar' ? 'إظهار للزوار' : 'Visible to visitors'}
                </label>
                <Toggle
                  value={form.is_visible}
                  onChange={() => setForm({ ...form, is_visible: !form.is_visible })}
                />
              </div>
              <div className="flex items-center justify-between">
                <label className="text-sm text-foreground">
                  {language === 'ar' ? 'إظهار الأسعار' : 'Show prices'}
                </label>
                <Toggle
                  value={form.show_price}
                  onChange={() => setForm({ ...form, show_price: !form.show_price })}
                />
              </div>
            </div>
          )}
        </div>

        <div className="flex gap-3 mt-6">
          <Button onClick={onSave} className="flex-1 bg-primary" disabled={saving}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : language === 'ar' ? 'حفظ' : 'Save'}
          </Button>
          <Button onClick={onClose} variant="outline" className="flex-1">
            {language === 'ar' ? 'إلغاء' : 'Cancel'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default OfferModal;
